import type { SSRData, Client, RequestPolicy } from '@urql/core'
import {
  createClient,
  ssrExchange,
  fetchExchange,
  cacheExchange,
} from '@urql/core'
import { ref } from 'vue'
import { offlineExchange } from '@urql/exchange-graphcache'
import { makeDefaultStorage } from '@urql/exchange-graphcache/default-storage'
import { useOnline } from '@vueuse/core'

const ssrKey = '__URQL_DATA__'

export default defineNuxtPlugin((nuxt) => {
  const online = useOnline()

  const config = useRuntimeConfig()
  if (!config.public.GQL_HOST) {
    throw new Error('GQL_HOST is not defined in runtime config')
  }

  const ssr = ssrExchange({
    isClient: import.meta.client,
  })

  if (import.meta.client) {
    nuxt.hook('app:created', () => {
      ssr.restoreData(nuxt.payload[ssrKey] as SSRData)
    })
  }

  if (import.meta.server) {
    nuxt.hook('app:rendered', () => {
      nuxt.payload[ssrKey] = ssr.extractData()
    })
  }

  // @ts-ignore: missing type
  const route = useRoute()

  //client only
  var cache = null
  if (import.meta.client) {
    const storage = makeDefaultStorage({
      idbName: 'desitingraphqlcache', // The name of the IndexedDB database
      maxAge: 180, // The maximum age of the persisted data in days
    })
    cache = offlineExchange({
      storage,
    })
  } else {
    cache = cacheExchange
  }

  /**
   * Because we are generating urls to elements in the "Allgemeine Infos" collection with a hash, it leads to malformed URLs like this:
   * /general_information#section-behandlungsvorschlaege-fur-ersthelfende-aerzteaerztinnen?live-preview=uPwAypdqSG66oVKs&token=END7sk1mMZXkJ6GpaPzZnnGfD9XBu8Xfp0J7cFId
   *
   * This is incorrect as the hash should be _after_ the query params.
   * It is very hard to fix server-side though, so we will have to deal with it here.
   */

  const queryParamsFoundInHash = route.hash?.split('?')[1]?.split('&') ?? []
  queryParamsFoundInHash.forEach((param) => {
    const [key, value] = param.split('=')
    route.query[key] = value
  })

  const isInLivePreview = !!route.query['live-preview']
  const exchanges = isInLivePreview ? [fetchExchange] : [cache, fetchExchange]
  let requestPolicy: RequestPolicy
  if (isInLivePreview) {
    requestPolicy = 'network-only'
  } else {
    requestPolicy = online ? 'cache-and-network' : 'cache-only'
  }

  const client = createClient({
    url:
      config.public.GQL_HOST +
      (route.query.token ? `?token=${route.query.token}` : ''),
    exchanges,
    requestPolicy,
  })

  nuxt.provide('urql', client)
  nuxt.vueApp.provide('$urql', ref(client))
})

declare module '#app' {
  interface NuxtApp {
    $urql: Client
  }
}
